import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, TR_MONTHS, TR_DAYS_SHORT, formatTurkishDate, formatTime } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Clock,
  User,
  Users,
  Phone,
  MapPin,
  Home,
  CheckCircle2,
  Send,
  Sparkles,
  PartyPopper,
  AlertTriangle,
  Heart,
  Image as ImageIcon,
} from "lucide-react";
import CategoryIcon from "@/components/CategoryIcon";
import InvitationUploader from "@/components/InvitationUploader";

const initialState = {
  category: "",
  date: "",
  time: "",
  whose_event: "",
  families: "",
  notifier_name: "",
  contact_number: "",
  venue: "",
  address: "",
  invitation_url: "",
};

const pad = (n) => String(n).padStart(2, "0");

const formatPhoneTR = (raw) => {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";
  let d = digits;
  if (d.length === 10 && d.startsWith("5")) d = "0" + d;
  const p = [];
  if (d.length > 0) p.push(d.slice(0, 1));
  if (d.length > 1) p.push(d.slice(1, 4));
  if (d.length > 4) p.push(d.slice(4, 7));
  if (d.length > 7) p.push(d.slice(7, 9));
  if (d.length > 9) p.push(d.slice(9, 11));
  return p.join(" ").trim();
};

const isValidPhone = (val) => (val || "").replace(/\D/g, "").length >= 10;

const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const startWeekDay = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const StepHeader = ({ step, total, title, subtitle }) => (
  <div className="pb-4 border-b border-stone-200/70">
    <div className="flex items-center justify-between mb-2.5">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">
        {step}/{total} · Adım
      </div>
      <div className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              i + 1 === step ? "w-8 bg-[#8A252C]" : i + 1 < step ? "w-4 bg-[#8A252C]/60" : "w-4 bg-stone-200"
            }`}
          />
        ))}
      </div>
    </div>
    <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2937]">{title}</h2>
    {subtitle && <p className="text-[15px] text-[#4B5563] mt-1">{subtitle}</p>}
  </div>
);

const FieldLabel = ({ icon: Icon, children, hint }) => (
  <div className="flex items-start gap-2 mb-2">
    {Icon && (
      <span className="mt-0.5 w-7 h-7 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-[#8A252C]" strokeWidth={2} />
      </span>
    )}
    <div>
      <Label className="text-base font-semibold text-[#1F2937] leading-tight block">{children}</Label>
      {hint && <p className="text-xs text-[#4B5563] mt-0.5">{hint}</p>}
    </div>
  </div>
);

const BigInput = (props) => (
  <Input
    {...props}
    className={`bg-stone-50 border-stone-300 h-14 text-base rounded-xl ${props.className || ""}`}
  />
);

const CategoryCards = ({ value, onChange, categories }) => (
  <div className="grid grid-cols-2 gap-2.5" data-testid="category-cards">
    {categories.map((c) => {
      const active = value === c.value;
      return (
        <button
          type="button"
          key={c.value}
          onClick={() => onChange(c.value)}
          data-testid={`category-card-${c.value}`}
          className={`relative flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
            active
              ? "border-[#8A252C] bg-[#8A252C]/5 shadow"
              : "border-stone-200 bg-white hover:border-[#8A252C]/40"
          }`}
        >
          <span
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-lg"
            style={{ backgroundColor: `${c.accent}15`, color: c.accent }}
          >
            <CategoryIcon icons={c.icons} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base font-bold text-[#1F2937] leading-tight">
              {c.label}
            </div>
            <div className="text-[11px] text-[#4B5563] leading-tight truncate">{c.hint}</div>
          </div>
          {active && (
            <CheckCircle2 className="w-5 h-5 text-[#8A252C] shrink-0" strokeWidth={2.5} />
          )}
        </button>
      );
    })}
  </div>
);

const DatePicker = ({ value, onChange, eventCountByDate = {} }) => {
  const today = new Date();
  const [view, setView] = useState(() => {
    if (value) {
      const [y, m] = value.split("-").map(Number);
      return { y, m: m - 1 };
    }
    return { y: today.getFullYear(), m: today.getMonth() };
  });

  const cells = useMemo(() => buildMonthGrid(view.y, view.m), [view]);
  const selected = value
    ? (() => {
        const [y, m, d] = value.split("-").map(Number);
        return { y, m: m - 1, d };
      })()
    : null;

  const prev = () => setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }));
  const next = () => setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }));

  const isPast = (d) => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return new Date(view.y, view.m, d) < t;
  };

  return (
    <div className="bg-white border-2 border-stone-200 rounded-2xl p-4" data-testid="date-picker">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={prev} className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="cal-prev-btn" aria-label="Önceki ay">
          <ChevronLeft className="w-5 h-5 text-[#4B5563]" />
        </button>
        <div className="font-display text-lg font-bold text-[#1F2937]" data-testid="cal-title">
          {TR_MONTHS[view.m]} {view.y}
        </div>
        <button type="button" onClick={next} className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="cal-next-btn" aria-label="Sonraki ay">
          <ChevronRight className="w-5 h-5 text-[#4B5563]" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {TR_DAYS_SHORT.map((d) => (
          <div key={d} className="text-center text-[11px] font-semibold text-[#4B5563] py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const past = isPast(d);
          const dateKey = `${view.y}-${pad(view.m + 1)}-${pad(d)}`;
          const eventCount = eventCountByDate[dateKey] || 0;
          const isSelected = selected && selected.y === view.y && selected.m === view.m && selected.d === d;
          return (
            <button
              type="button"
              key={i}
              disabled={past}
              data-testid={`cal-day-${view.y}-${view.m + 1}-${d}`}
              onClick={() => onChange(dateKey)}
              className={`relative h-11 rounded-lg text-sm font-semibold transition-all ${
                isSelected ? "bg-[#8A252C] text-white shadow"
                  : past ? "text-stone-300 cursor-not-allowed"
                  : eventCount > 0 ? "text-[#1F2937] bg-[#D4AF37]/15 hover:bg-[#D4AF37]/25 border border-[#D4AF37]/40"
                  : "text-[#1F2937] hover:bg-[#8A252C]/10"
              }`}
            >
              {d}
              {eventCount > 0 && (
                <span
                  className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${
                    isSelected ? "bg-white text-[#8A252C]" : "bg-[#8A252C] text-white"
                  }`}
                >
                  {eventCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TimePicker = ({ value, onChange }) => {
  const [h = "", mi = ""] = (value || "").split(":");
  const hours = Array.from({ length: 24 }, (_, i) => pad(i));
  const mins = ["00", "15", "30", "45"];
  const set = (hh, mm) => onChange(`${hh || "00"}:${mm || "00"}`);
  return (
    <div className="bg-white border-2 border-stone-200 rounded-2xl p-4" data-testid="time-picker">
      <div className="flex items-center gap-3 justify-center">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-bold mb-1 text-center">Saat</div>
          <select value={h} onChange={(e) => set(e.target.value, mi || "00")} data-testid="time-hour-select"
            className="h-14 w-20 rounded-xl border-2 border-stone-200 bg-stone-50 text-center text-xl font-semibold text-[#1F2937] focus:border-[#8A252C] outline-none">
            <option value="" disabled>--</option>
            {hours.map((hh) => <option key={hh} value={hh}>{hh}</option>)}
          </select>
        </div>
        <div className="text-3xl font-bold text-[#8A252C] mt-6">:</div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-bold mb-1 text-center">Dakika</div>
          <select value={mi} onChange={(e) => set(h || "00", e.target.value)} data-testid="time-minute-select"
            className="h-14 w-20 rounded-xl border-2 border-stone-200 bg-stone-50 text-center text-xl font-semibold text-[#1F2937] focus:border-[#8A252C] outline-none">
            <option value="" disabled>--</option>
            {mins.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 justify-center">
        {["10:00", "14:00", "17:00", "19:00", "20:00", "21:00"].map((t) => (
          <button key={t} type="button" onClick={() => onChange(t)} data-testid={`time-quick-${t}`}
            className={`px-3 h-9 rounded-full text-sm font-semibold border transition ${
              value === t ? "bg-[#8A252C] text-white border-[#8A252C]"
                : "bg-white border-stone-300 text-[#4B5563] hover:border-[#8A252C]/50"
            }`}>
            {t}
          </button>
        ))}
      </div>
    </div>
  );
};

const SummaryRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-stone-100 last:border-0">
    <span className="mt-0.5 w-8 h-8 rounded-full bg-[#8A252C]/10 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-[#8A252C]" strokeWidth={2} />
    </span>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">{label}</div>
      <div className="text-[15px] text-[#1F2937] break-words">{value || "-"}</div>
    </div>
  </div>
);

const TOTAL_STEPS = 4;

/**
 * EventForm - 4 step wizard.
 * @param {Function} onCreated  Called with created event
 * @param {Function} onSubmit   Optional override of the create call (used by admin manual add)
 * @param {String}   submitLabel Optional submit button label
 * @param {String}   successText Optional success screen text
 */
const EventForm = ({ onCreated, onSubmit, submitLabel = "Evet, Gönder", successText }) => {
  const { categories } = useSettings();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [existingEvents, setExistingEvents] = useState([]);
  const [conflict, setConflict] = useState(null);
  const [confirmedDates, setConfirmedDates] = useState({});
  const [invPrompt, setInvPrompt] = useState(null); // null | 'ask' | 'upload'

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    let alive = true;
    api.get("/events").then((r) => {
      if (alive) setExistingEvents((r.data || []).filter((e) => e.status !== "rejected"));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const eventCountByDate = useMemo(() => {
    const map = {};
    for (const ev of existingEvents) {
      const d = new Date(ev.event_datetime);
      if (isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      map[key] = (map[key] || 0) + 1;
    }
    return map;
  }, [existingEvents]);

  const eventsOnDate = (dateStr) => {
    if (!dateStr) return [];
    return existingEvents.filter((ev) => {
      const d = new Date(ev.event_datetime);
      if (isNaN(d.getTime())) return false;
      const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      return key === dateStr;
    });
  };

  const canNext = useMemo(() => {
    if (step === 1) return form.category && form.date && form.time;
    if (step === 2)
      return (
        form.whose_event.trim() && form.families.trim() &&
        form.notifier_name.trim() && isValidPhone(form.contact_number)
      );
    if (step === 3) return form.venue.trim() && form.address.trim();
    return true;
  }, [step, form]);

  const goNext = () => {
    if (!canNext) { toast.error("Lütfen bu adımdaki alanları doldurun"); return; }
    if (step === 1 && !confirmedDates[form.date]) {
      const clash = eventsOnDate(form.date);
      if (clash.length > 0) { setConflict({ date: form.date, events: clash }); return; }
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));
  const acceptConflict = () => {
    setConfirmedDates((c) => ({ ...c, [conflict.date]: true }));
    setConflict(null);
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  // When user clicks "Evet, Gönder" on step 4 → first ask about invitation
  const requestSubmit = () => setInvPrompt("ask");

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
      const payload = {
        category: form.category,
        event_datetime: iso,
        whose_event: form.whose_event.trim(),
        families: form.families.trim(),
        venue: form.venue.trim(),
        address: form.address.trim(),
        notifier_name: form.notifier_name.trim(),
        contact_number: form.contact_number.trim(),
        invitation_url: form.invitation_url || null,
      };
      const submitter = onSubmit || ((p) => api.post("/events", p).then((r) => r.data));
      const data = await submitter(payload);
      setSuccess(true);
      setInvPrompt(null);
      onCreated?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bir hata oluştu, tekrar deneyin");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setForm(initialState);
    setStep(1);
    setSuccess(false);
    setConfirmedDates({});
    setInvPrompt(null);
    api.get("/events").then((r) => setExistingEvents((r.data || []).filter((e) => e.status !== "rejected"))).catch(() => {});
  };

  if (success) {
    return (
      <div data-testid="event-form-success" className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 sm:p-12 text-center animate-fade-up">
        <div className="w-20 h-20 rounded-full bg-[#4A5D23]/10 flex items-center justify-center mx-auto mb-5">
          <PartyPopper className="w-10 h-10 text-[#4A5D23]" strokeWidth={1.75} />
        </div>
        <h3 className="font-display text-3xl font-bold text-[#1F2937]">Etkinliğiniz Alındı!</h3>
        <p className="text-[#4B5563] mt-3 text-base max-w-md mx-auto">
          {successText || "Bildiriminiz yönetici onayı bekliyor. Onaylandıktan sonra derneğin etkinlik takviminde görünecek. Elinize sağlık!"}
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Button onClick={resetAll} data-testid="add-another-btn" className="bg-[#8A252C] hover:bg-[#6f1d24] text-white rounded-xl h-12 px-6 gap-2">
            <Sparkles className="w-4 h-4" />
            Yeni Etkinlik Bildir
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="event-form" className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 sm:p-8 space-y-6 animate-fade-up">
      {step === 1 && (
        <div className="space-y-5" data-testid="step-1">
          <StepHeader step={1} total={TOTAL_STEPS} title="Nasıl bir etkinlik?" subtitle="Etkinlik türünü, tarihini ve saatini seçin." />
          <CategoryCards value={form.category} onChange={(v) => set("category", v)} categories={categories} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <FieldLabel icon={CalendarDays} hint="Takvimden gün seçin. Sarı: mevcut etkinlik var.">Etkinlik Tarihi</FieldLabel>
              <DatePicker value={form.date} onChange={(v) => set("date", v)} eventCountByDate={eventCountByDate} />
            </div>
            <div>
              <FieldLabel icon={Clock} hint="Saat ve dakikayı seçin veya hızlı seçim yapın">Etkinlik Saati</FieldLabel>
              <TimePicker value={form.time} onChange={(v) => set("time", v)} />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5" data-testid="step-2">
          <StepHeader step={2} total={TOTAL_STEPS} title="Kimin etkinliği?" subtitle="Etkinlik sahibi ve iletişim bilgileri." />
          <div><FieldLabel icon={Heart} hint="Örn: Ayşe & Mehmet">Kimin Etkinliği</FieldLabel>
            <BigInput value={form.whose_event} onChange={(e) => set("whose_event", e.target.value)} placeholder="Ayşe & Mehmet" data-testid="event-whose-input" /></div>
          <div><FieldLabel icon={Users} hint="Örn: Yılmaz — Demir Aileleri">Aileleri</FieldLabel>
            <BigInput value={form.families} onChange={(e) => set("families", e.target.value)} placeholder="Yılmaz — Demir Aileleri" data-testid="event-families-input" /></div>
          <div><FieldLabel icon={User} hint="Bu formu dolduran kişi">Bildirenin Adı Soyadı</FieldLabel>
            <BigInput value={form.notifier_name} onChange={(e) => set("notifier_name", e.target.value)} placeholder="Ad Soyad" data-testid="event-notifier-input" /></div>
          <div><FieldLabel icon={Phone} hint="Ulaşabileceğimiz bir telefon numarası">İletişim Numarası</FieldLabel>
            <BigInput value={form.contact_number} onChange={(e) => set("contact_number", formatPhoneTR(e.target.value))} placeholder="0 5xx xxx xx xx" inputMode="tel" data-testid="event-contact-input" />
            {form.contact_number && !isValidPhone(form.contact_number) && (
              <p className="text-xs text-[#7F1D1D] mt-1.5">Lütfen 10 haneli geçerli bir numara girin.</p>
            )}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5" data-testid="step-3">
          <StepHeader step={3} total={TOTAL_STEPS} title="Nerede yapılacak?" subtitle="Etkinlik mekanı ve adres bilgisi." />
          <div><FieldLabel icon={Home} hint="Örn: Salon Grand Palace">Mekan</FieldLabel>
            <BigInput value={form.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Salon adı / mekan" data-testid="event-venue-input" /></div>
          <div><FieldLabel icon={MapPin} hint="Cadde, sokak, ilçe, il bilgilerini yazın">Adres</FieldLabel>
            <Textarea rows={4} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Örn: Atatürk Cad. No:15 Merkez / Şanlıurfa" data-testid="event-address-input" className="bg-stone-50 border-stone-300 rounded-xl resize-none text-base" /></div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-5" data-testid="step-4">
          <StepHeader step={4} total={TOTAL_STEPS} title="Bilgiler doğru mu?" subtitle="Göndermeden önce lütfen kontrol edin." />
          <div className="bg-stone-50 rounded-2xl border border-stone-200 p-5">
            <SummaryRow icon={Sparkles} label="Etkinlik Türü" value={categories.find((c) => c.value === form.category)?.label} />
            <SummaryRow icon={CalendarDays} label="Tarih & Saat" value={form.date && form.time ? formatTurkishDate(new Date(`${form.date}T${form.time}:00`).toISOString()) : ""} />
            <SummaryRow icon={Heart} label="Kimin Etkinliği" value={form.whose_event} />
            <SummaryRow icon={Users} label="Aileleri" value={form.families} />
            <SummaryRow icon={Home} label="Mekan" value={form.venue} />
            <SummaryRow icon={MapPin} label="Adres" value={form.address} />
            <SummaryRow icon={User} label="Bildiren" value={form.notifier_name} />
            <SummaryRow icon={Phone} label="İletişim" value={form.contact_number} />
            {form.invitation_url && (
              <SummaryRow icon={ImageIcon} label="Davetiye" value="Davetiye görseli yüklendi" />
            )}
          </div>
          <p className="text-sm text-[#4B5563] text-center">
            Bir yerde hata mı var?{" "}
            <button type="button" onClick={() => setStep(1)} data-testid="edit-from-summary-btn" className="text-[#8A252C] font-semibold underline underline-offset-2">Baştan düzenle</button>
          </p>
        </div>
      )}

      <div className="pt-3 border-t border-stone-200/70 flex items-center justify-between gap-3">
        <Button type="button" onClick={goBack} disabled={step === 1 || submitting} data-testid="wizard-back-btn"
          variant="outline" className="rounded-xl h-12 px-5 gap-2 border-stone-300 text-[#4B5563] hover:bg-stone-100 disabled:opacity-40">
          <ChevronLeft className="w-4 h-4" />
          Geri
        </Button>
        {step < TOTAL_STEPS ? (
          <Button type="button" onClick={goNext} disabled={!canNext} data-testid="wizard-next-btn"
            className="bg-[#8A252C] hover:bg-[#6f1d24] text-white rounded-xl h-12 px-6 gap-2 shadow-sm disabled:opacity-50">
            İleri <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button type="button" onClick={requestSubmit} disabled={submitting} data-testid="submit-event-btn"
            className="bg-[#4A5D23] hover:bg-[#3e4e1d] text-white rounded-xl h-12 px-6 gap-2 shadow-sm">
            <Send className="w-4 h-4" />
            {submitting ? "Gönderiliyor..." : submitLabel}
          </Button>
        )}
      </div>

      {/* Duplicate day dialog */}
      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="max-w-md bg-[#F9F6F0] border-stone-200" data-testid="conflict-dialog">
          <DialogHeader className="text-left">
            <div className="w-12 h-12 rounded-full bg-[#D97706]/15 flex items-center justify-center mb-2">
              <AlertTriangle className="w-6 h-6 text-[#D97706]" />
            </div>
            <DialogTitle className="font-display text-2xl text-[#1F2937]">Bu tarihte etkinlik var</DialogTitle>
            <p className="text-sm text-[#4B5563]">
              Seçtiğiniz{" "}
              <span className="font-semibold text-[#1F2937]">
                {conflict && (() => { const [y, m, d] = conflict.date.split("-").map(Number); return `${d} ${TR_MONTHS[m - 1]} ${y}`; })()}
              </span>{" "}
              tarihinde{" "}
              <span className="font-semibold text-[#8A252C]">{conflict?.events.length} etkinlik</span>{" "}
              bulunuyor. Yine de eklemek ister misiniz?
            </p>
          </DialogHeader>
          <div className="max-h-52 overflow-y-auto space-y-2 py-2">
            {conflict?.events.map((ev) => {
              const cat = categories.find((c) => c.value === ev.category);
              return (
                <div key={ev.id} data-testid={`conflict-event-${ev.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-stone-200">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center text-base"
                    style={{ backgroundColor: `${cat?.accent}15`, color: cat?.accent }}>
                    <CategoryIcon icons={cat?.icons} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1F2937] truncate">
                      {cat?.label} · {formatTime(ev.event_datetime)}
                    </div>
                    <div className="text-xs text-[#4B5563] truncate">{ev.whose_event} — {ev.venue}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setConflict(null)} data-testid="conflict-change-date-btn" className="border-stone-300 h-11">Tarihi Değiştir</Button>
            <Button onClick={acceptConflict} data-testid="conflict-continue-btn" className="bg-[#8A252C] hover:bg-[#6f1d24] text-white h-11 gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Yine de Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invitation prompt & uploader dialog */}
      <Dialog open={!!invPrompt} onOpenChange={(o) => !o && !submitting && setInvPrompt(null)}>
        <DialogContent className="max-w-lg bg-[#F9F6F0] border-stone-200" data-testid="invitation-dialog">
          <DialogHeader className="text-left">
            <div className="w-12 h-12 rounded-full bg-[#8A252C]/10 flex items-center justify-center mb-2">
              <ImageIcon className="w-6 h-6 text-[#8A252C]" />
            </div>
            <DialogTitle className="font-display text-2xl text-[#1F2937]">
              {invPrompt === "upload" ? "Davetiye Yükle" : "Davetiye yüklemek ister misiniz?"}
            </DialogTitle>
            <p className="text-sm text-[#4B5563]">
              {invPrompt === "upload"
                ? "Davetiye görselini seçip yükleyin. İsterseniz sonra kaldırabilirsiniz."
                : "Etkinliğinizin davetiyesi varsa görselini yükleyebilirsiniz. Bu adım isteğe bağlıdır."}
            </p>
          </DialogHeader>

          {invPrompt === "upload" && (
            <div className="py-2">
              <InvitationUploader
                value={form.invitation_url}
                onChange={(url) => set("invitation_url", url)}
                compact
              />
            </div>
          )}

          <DialogFooter className="gap-2 flex-col sm:flex-row">
            {invPrompt === "ask" ? (
              <>
                <Button variant="outline" onClick={doSubmit} disabled={submitting}
                  data-testid="inv-skip-btn"
                  className="border-stone-300 h-11">
                  Hayır, direkt gönder
                </Button>
                <Button onClick={() => setInvPrompt("upload")}
                  data-testid="inv-yes-btn"
                  className="bg-[#8A252C] hover:bg-[#6f1d24] text-white h-11 gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Evet, Yükle
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { set("invitation_url", ""); setInvPrompt("ask"); }}
                  data-testid="inv-back-btn" className="border-stone-300 h-11">
                  Vazgeç
                </Button>
                <Button onClick={doSubmit} disabled={submitting}
                  data-testid="inv-upload-submit-btn"
                  className="bg-[#4A5D23] hover:bg-[#3e4e1d] text-white h-11 gap-2">
                  <Send className="w-4 h-4" />
                  {submitting ? "Gönderiliyor..." : "Gönder"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventForm;
